import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { withBranch } from '../api/branch';
import { apiFetch } from '../api/client';
import type { BranchHead, GroupBase, GroupContent } from '../api/types';
import { LocaleSelector } from '../components/LocaleSelector';
import { ROW_ID_PREFIX, rowDomId } from '../components/rowId';
import { useVisibleLocales } from '../hooks/useVisibleLocales';
import { useViewBranch } from '../view/ViewBranchContext';

type GroupResponse = {
  branch: string;
  group: GroupContent;
};

type LocalEdits = Record<string, Record<string, string>>;

// branch HEAD を polling する間隔。GitHub API の rate limit (5000 req/h) と
// 編集中の体感ラグのバランスで 30 秒にしている。
const HEAD_POLL_INTERVAL_MS = 30_000;

export const Group = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { viewBranch, isReadOnly } = useViewBranch();

  const [edits, setEdits] = useState<LocalEdits>({});
  // 検索からの deep link 時に一時ハイライトする key
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  // 編集を開始した時点の remote 値スナップショット。conflict 判定 (= remote が
  // 編集開始時から書き換わったか) のためだけに保持し、保存・破棄でクリアする。
  const [baseSnapshot, setBaseSnapshot] = useState<LocalEdits>({});
  const [savingLocale, setSavingLocale] = useState<string | null>(null);
  // 「main から変わっている key だけ」に絞り込む表示トグル
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [remoteUpdatedAt, setRemoteUpdatedAt] = useState<Date | null>(null);
  const { visible, toggle } = useVisibleLocales();

  const { data, isLoading, error } = useQuery({
    queryKey: ['group', groupId, viewBranch],
    queryFn: () => apiFetch<GroupResponse>(withBranch(`/api/files/${groupId ?? ''}`, viewBranch)),
    enabled: groupId !== undefined,
  });

  // 30 秒間隔で branch HEAD SHA だけ取りに行く軽量 polling。
  // HEAD が前回から変わっていたら、詳細 (locales / entries / 各 file SHA) を再 fetch する。
  // ウィンドウが背面の間は polling しない (refetchIntervalInBackground: false がデフォルト)。
  const { data: headData } = useQuery({
    queryKey: ['group-head', groupId, viewBranch],
    queryFn: () => apiFetch<BranchHead>(withBranch(`/api/files/${groupId ?? ''}/head`, viewBranch)),
    enabled: groupId !== undefined,
    refetchInterval: HEAD_POLL_INTERVAL_MS,
  });

  // main の現在値。編集画面で「main からの変更」をハイライトするために使う。branch HEAD
  // polling とは独立に変わるので別 query で持ち、staleTime を長くして branch 側の再 fetch
  // (polling / 保存後の invalidate) のたびに main を取り直さないようにする。
  const { data: baseData } = useQuery({
    queryKey: ['group-base', groupId],
    queryFn: () => apiFetch<GroupBase>(`/api/files/${groupId ?? ''}/base`),
    enabled: groupId !== undefined,
    staleTime: 5 * 60_000,
  });

  // groupId が変わったら、前グループの「リモート更新を反映しました」表示を引きずらない
  // ようリセットする。effect 内 setState は cascading render を招くため、render 中に
  // 前回の groupId と比較して行う (React 公式の「prop 変化に応じた state リセット」パターン)。
  const [trackedGroupId, setTrackedGroupId] = useState(groupId);
  if (trackedGroupId !== groupId) {
    setTrackedGroupId(groupId);
    setRemoteUpdatedAt(null);
  }

  // 目線 (閲覧ブランチ) を切り替えたら、手元の未保存編集をクリアする。別ブランチのデータに
  // 前ブランチの編集が重なって表示される・誤保存される事故を防ぐ。groupId リセットと同じく
  // render 中の前回値比較で行い、effect 内 setState (cascading render) を避ける。
  const [trackedViewBranch, setTrackedViewBranch] = useState(viewBranch);
  if (trackedViewBranch !== viewBranch) {
    setTrackedViewBranch(viewBranch);
    setEdits({});
    setBaseSnapshot({});
    setFeedback(null);
    setRemoteUpdatedAt(null);
  }

  // branch HEAD polling の baseline SHA。groupId / viewBranch も一緒に持ち、別グループや別
  // ブランチへ切り替えた直後は前コンテキストの SHA を「変化した」と誤判定しないよう baseline
  // を取り直すだけにする (Group コンポーネントは再利用されるため両者の突き合わせが要る)。
  const headBaselineRef = useRef<{
    groupId: string | undefined;
    viewBranch: string | null;
    sha: string;
  } | null>(null);
  useEffect(() => {
    if (headData === undefined) return;
    const baseline = headBaselineRef.current;
    if (baseline === null || baseline.groupId !== groupId || baseline.viewBranch !== viewBranch) {
      headBaselineRef.current = { groupId, viewBranch, sha: headData.sha };
      return;
    }
    if (baseline.sha === headData.sha) return;
    headBaselineRef.current = { groupId, viewBranch, sha: headData.sha };
    setRemoteUpdatedAt(new Date());
    void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
  }, [headData, groupId, viewBranch, queryClient]);

  const visibleLocales = useMemo(() => {
    if (data === undefined) return [];
    return data.group.locales.filter(
      (l) => l.locale === data.group.baseLocale || visible.has(l.locale),
    );
  }, [data, visible]);

  // edits は「ユーザーが触った key だけ」を保持する sparse map にしている。
  // remote の値は data.group.locales[].entries から都度読むため、polling refetch で
  // 未編集の key は自動的に最新 remote に追従する。サーバー再 fetch 時のここでの同期は不要。

  const allKeys = useMemo(() => {
    if (data === undefined) return [];
    const keys = new Set<string>();
    for (const l of data.group.locales) {
      for (const key of Object.keys(l.entries)) keys.add(key);
    }
    return Array.from(keys).sort();
  }, [data]);

  // main の現在値と比べて「main から変わっている」セル。key → 変更のある locale 集合。
  // 現在値は「branch の保存済み値 + 未保存 edits」なので、未公開の保存済み差分も未保存編集も
  // まとめて『main から触った箇所』として扱う。baseData 未ロード時は判定不能なので空にする。
  const changedKeys = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (data === undefined || baseData === undefined) return m;
    for (const l of data.group.locales) {
      const base = baseData.entries[l.locale] ?? {};
      const editsForLocale = edits[l.locale] ?? {};
      for (const key of allKeys) {
        const current = editsForLocale[key] ?? l.entries[key] ?? '';
        if (current !== (base[key] ?? '')) {
          const set = m.get(key) ?? new Set<string>();
          set.add(l.locale);
          m.set(key, set);
        }
      }
    }
    return m;
  }, [data, baseData, edits, allKeys]);

  // locale → main から変わっているセル数。言語ピルのバッジ用 (非表示 locale も含めて算出)
  const changedCountByLocale = useMemo(() => {
    const m = new Map<string, number>();
    for (const set of changedKeys.values()) {
      for (const l of set) m.set(l, (m.get(l) ?? 0) + 1);
    }
    return m;
  }, [changedKeys]);

  // 表示中の locale で main から変わっているセルの総数 (上部サマリ用)
  const totalChanged = useMemo(() => {
    let n = 0;
    for (const set of changedKeys.values()) {
      for (const l of visibleLocales) if (set.has(l.locale)) n++;
    }
    return n;
  }, [changedKeys, visibleLocales]);

  // 「変更のみ表示」時は、表示中の locale のどれかで main から変わっている key だけ残す
  const displayKeys = useMemo(() => {
    if (!showChangedOnly) return allKeys;
    return allKeys.filter((key) => {
      const set = changedKeys.get(key);
      return set !== undefined && visibleLocales.some((l) => set.has(l.locale));
    });
  }, [showChangedOnly, allKeys, changedKeys, visibleLocales]);

  // locale ごとの未保存件数。edits は sparse map (触った key だけ) なので、
  // remote 値と異なる entries の数をカウントすれば dirty 件数になる。
  const dirtyByLocale = useMemo(() => {
    const m = new Map<string, number>();
    if (data === undefined) return m;
    for (const l of data.group.locales) {
      const current = edits[l.locale];
      if (current === undefined) continue;
      let count = 0;
      for (const [k, v] of Object.entries(current)) {
        if ((l.entries[k] ?? '') !== v) count++;
      }
      if (count > 0) m.set(l.locale, count);
    }
    return m;
  }, [data, edits]);

  const totalDirty = useMemo(
    () => Array.from(dirtyByLocale.values()).reduce((a, b) => a + b, 0),
    [dirtyByLocale],
  );

  // 編集中の key のうち、編集開始時の remote 値から現在の remote 値が変わっているもの。
  // = 他セッション (別タブ・別端末・main からの sync) が同じ key を書き換えた conflict。
  // 保存すると自分の編集で remote の変更を上書きするので警告を出す。
  const conflicts = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (data === undefined) return m;
    for (const l of data.group.locales) {
      const editsForLocale = edits[l.locale];
      const baseForLocale = baseSnapshot[l.locale];
      if (editsForLocale === undefined || baseForLocale === undefined) continue;
      const set = new Set<string>();
      for (const [k, v] of Object.entries(editsForLocale)) {
        // dirty (= 編集中で、現在の remote と値が違う) かつ
        // remote が編集開始時から変わっている key だけが conflict
        if ((l.entries[k] ?? '') === v) continue;
        const base = baseForLocale[k];
        if (base === undefined) continue;
        if (base !== (l.entries[k] ?? '')) set.add(k);
      }
      if (set.size > 0) m.set(l.locale, set);
    }
    return m;
  }, [data, edits, baseSnapshot]);

  const totalConflicts = useMemo(
    () => Array.from(conflicts.values()).reduce((a, s) => a + s.size, 0),
    [conflicts],
  );

  const save = useMutation({
    mutationFn: async (locale: string) => {
      if (data === undefined) throw new Error('not loaded');
      const localeData = data.group.locales.find((l) => l.locale === locale);
      if (localeData === undefined) throw new Error('locale not found');
      // edits は sparse なので remote 値とマージして全 entries を送信する
      const entries = { ...localeData.entries, ...(edits[locale] ?? {}) };
      const files = localeData.files.map((f) => ({ name: f.name, sha: f.sha }));
      return await apiFetch<{ files: { name: string; sha: string }[] }>(
        `/api/files/${data.group.id}/${locale}`,
        {
          method: 'PUT',
          body: JSON.stringify({ entries, files }),
        },
      );
    },
    onMutate: (locale) => {
      setSavingLocale(locale);
      setFeedback(null);
    },
    onSuccess: (_data, locale) => {
      setFeedback({ type: 'ok', text: '保存しました' });
      // 保存した locale の編集状態と編集開始 snapshot をクリアする
      const omitLocale = (prev: LocalEdits): LocalEdits =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => k !== locale));
      setEdits(omitLocale);
      setBaseSnapshot(omitLocale);
      void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      // 保存で branch が main より進むので、公開ボタンが見る sync-status と一覧カードの
      // 変更バッジ (files) も更新する。これがないと保存直後はリロードするまで公開ボタンが
      // 有効化されない。
      void queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      void queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (e: Error) => {
      setFeedback({ type: 'error', text: e.message });
      // 部分 conflict (= 一部ファイルが先に commit されて残りが 409) の場合、
      // backend は更新済み sha を返さないが GitHub 側は新 sha を持っている。
      // 再 fetch しないとクライアントの sha が古いままで永久 409 ループに入る。
      void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      // 部分 commit で branch が進んでいる可能性があるので sync-status / files も更新する
      void queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      void queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onSettled: () => {
      setSavingLocale(null);
    },
  });

  // dirty な locale を順次保存。途中失敗時は以降を打ち切る (onError 側で feedback 設定済み)
  const saveAll = useCallback(async () => {
    const locales = Array.from(dirtyByLocale.keys());
    for (const l of locales) {
      try {
        await save.mutateAsync(l);
      } catch {
        return;
      }
    }
  }, [dirtyByLocale, save]);

  const discard = useCallback(() => {
    if (data === undefined) return;
    if (!window.confirm('未保存の変更を破棄しますか？')) return;
    setEdits({});
    setBaseSnapshot({});
    setFeedback(null);
  }, [data]);

  // 検索 deep link (#row:<encoded-key>) で該当行までスクロール + 一時ハイライト。
  // data が来てから DOM が出るので [data, location.hash] 両方を依存に入れる。
  useEffect(() => {
    if (data === undefined) return;
    const hash = location.hash;
    if (hash === '') return;
    const id = hash.slice(1);
    if (!id.startsWith(ROW_ID_PREFIX)) return;
    let key: string;
    try {
      key = decodeURIComponent(id.slice(ROW_ID_PREFIX.length));
    } catch {
      return;
    }
    const el = document.getElementById(id);
    if (el === null) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // ハイライトは scroll に伴う一時的な視覚フィードバック。effect 同期内で setState すると
    // cascading render になるため、次フレームで点灯させ 2.5 秒後に消灯する。
    const raf = requestAnimationFrame(() => setHighlightKey(key));
    const t = window.setTimeout(() => setHighlightKey(null), 2500);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [data, location.hash]);

  // ⌘S / Ctrl+S で dirty 全保存。textarea にフォーカスがあっても拾えるよう window で listen
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 's') return;
      e.preventDefault();
      if (isReadOnly || totalDirty === 0 || save.isPending) return;
      void saveAll();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isReadOnly, totalDirty, save.isPending, saveAll]);

  if (isLoading) return <div className="text-sm text-gray-500">読み込み中...</div>;
  if (error !== null) return <div className="text-sm text-red-700">{error.message}</div>;
  if (data === undefined) return null;

  const setEntry = (locale: string, key: string, value: string) => {
    if (isReadOnly) return; // 他者ブランチ閲覧中は編集不可
    setEdits((prev) => ({
      ...prev,
      [locale]: { ...(prev[locale] ?? {}), [key]: value },
    }));
    // 初回入力時に、その時点の remote 値を base として記録する。
    // 以降は触らない (= 編集開始時の値を保持して conflict 判定に使う)。
    setBaseSnapshot((prev) => {
      const existing = prev[locale]?.[key];
      if (existing !== undefined) return prev;
      const remoteValue = data.group.locales.find((l) => l.locale === locale)?.entries[key] ?? '';
      return {
        ...prev,
        [locale]: { ...(prev[locale] ?? {}), [key]: remoteValue },
      };
    });
  };

  return (
    <div className="space-y-4 pb-20">
      <div>
        <Link to="/" className="text-xs text-gray-500 hover:underline">
          ← 一覧に戻る
        </Link>
        <h2 className="mt-1 text-lg font-semibold">{data.group.name}</h2>
        <p className="text-xs text-gray-500">
          base: {data.group.baseLocale} / type: {data.group.fileType}
        </p>
      </div>

      {isReadOnly && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span className="font-semibold">👁 閲覧モード</span>
          <span className="ml-2">
            他ユーザーのブランチ (<code>{data.branch}</code>) を閲覧中です。編集はできません。自分の
            目線に戻すには右上のブランチ切り替えから「(自分)」を選んでください。
          </span>
        </div>
      )}

      <LocaleSelector
        baseLocale={data.group.baseLocale}
        allLocales={data.group.locales.map((l) => l.locale)}
        visible={visible}
        onToggle={toggle}
        changedCountByLocale={changedCountByLocale}
      />

      {feedback !== null && (
        <p className={`text-xs ${feedback.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
          {feedback.text}
        </p>
      )}

      {totalConflicts > 0 && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          <span className="font-semibold">
            ⚠ 編集中の {totalConflicts} 件の key が、別の場所で書き換わりました。
          </span>
          <span className="ml-2">
            保存するとリモートの変更を上書きします。該当行は赤でハイライトされています。
          </span>
        </div>
      )}

      {totalConflicts === 0 && remoteUpdatedAt !== null && (
        <p className="text-xs text-gray-500">
          {remoteUpdatedAt.toLocaleTimeString()} にリモートの更新を反映しました
        </p>
      )}

      <div className="flex items-center gap-3">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showChangedOnly}
            onChange={(e) => setShowChangedOnly(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          変更のみ表示
        </label>
        {totalChanged > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            main から {totalChanged} 件変更
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded border bg-white">
        <table className="min-w-full table-fixed border-collapse text-sm">
          <thead className="border-b bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="w-64 px-3 py-2 text-left">key</th>
              {visibleLocales.map((l) => {
                const dirty = dirtyByLocale.get(l.locale) ?? 0;
                return (
                  <th key={l.locale} className="px-3 py-2 text-left">
                    <div className="flex items-center gap-2">
                      <span>
                        {l.locale}
                        {l.locale === data.group.baseLocale && (
                          <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] text-gray-700">
                            base
                          </span>
                        )}
                      </span>
                      {dirty > 0 && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                          {dirty} 件編集中
                        </span>
                      )}
                      {savingLocale === l.locale && (
                        <span className="text-[10px] text-gray-500">保存中...</span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayKeys.map((key) => {
              const changedLocales = changedKeys.get(key);
              const rowChanged =
                changedLocales !== undefined &&
                visibleLocales.some((l) => changedLocales.has(l.locale));
              return (
                <tr
                  key={key}
                  id={rowDomId(key)}
                  className={`scroll-mt-20 border-b align-top transition-colors ${
                    highlightKey === key ? 'bg-yellow-50' : ''
                  }`}
                >
                  <td className="px-3 py-2 font-mono text-xs break-all text-gray-600">
                    <span className="flex items-start gap-1.5">
                      <span
                        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                          rowChanged ? 'bg-amber-500' : 'bg-transparent'
                        }`}
                        title={rowChanged ? 'main から変更されています' : undefined}
                      />
                      <span>{key}</span>
                    </span>
                  </td>
                  {visibleLocales.map((l) => {
                    const value = edits[l.locale]?.[key] ?? l.entries[key] ?? '';
                    const isConflict = conflicts.get(l.locale)?.has(key) ?? false;
                    const isChanged = changedLocales?.has(l.locale) ?? false;
                    return (
                      <td key={l.locale} className="px-3 py-2">
                        <textarea
                          value={value}
                          onChange={(e) => setEntry(l.locale, key, e.target.value)}
                          rows={1}
                          // 他者ブランチ閲覧時は readOnly のみ (disabled にしない)。
                          // disabled はテキスト選択も不可にし「訳をコピーして参考にする」用途を
                          // 損なうため。編集の抑止は readOnly + setEntry 側のガードで担保する。
                          readOnly={isReadOnly}
                          className={`[field-sizing:content] w-full resize-y rounded border px-2 py-1 text-sm ${
                            isConflict
                              ? 'border-red-400 bg-red-50'
                              : isChanged
                                ? 'border-amber-300 bg-amber-50'
                                : ''
                          } ${isReadOnly ? 'cursor-default border-transparent bg-gray-50 text-gray-600' : ''}`}
                        />
                        {isConflict && (
                          <p
                            className="mt-1 text-[10px] text-red-700"
                            title={baseSnapshot[l.locale]?.[key] ?? ''}
                          >
                            リモートが「{l.entries[key] ?? ''}」に変わりました
                          </p>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {displayKeys.length === 0 && (
              <tr>
                <td
                  colSpan={visibleLocales.length + 1}
                  className="px-3 py-6 text-center text-xs text-gray-500"
                >
                  main から変更された項目はありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isReadOnly && totalDirty > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-3">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{totalDirty}</span> 件の未保存変更
              <span className="ml-2 text-xs text-gray-500">
                (
                {Array.from(dirtyByLocale.entries())
                  .map(([l, n]) => `${l} ${n}`)
                  .join(' / ')}
                )
              </span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={discard}
                disabled={save.isPending}
                className="rounded border px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                破棄
              </button>
              <button
                type="button"
                onClick={() => void saveAll()}
                disabled={save.isPending}
                className="rounded bg-gray-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {save.isPending ? '保存中...' : '保存 (⌘S)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
